import { Text, View } from '@react-pdf/renderer'
import { Style } from '@react-pdf/types'
import moment from 'moment'
import React from 'react'
import { parseFormLabelText, parseSfFieldValue } from '../../common/Common'
import { pdfDefaultFontSize } from '../../pdf-components/PDFCommon'
import { PdfSplittableTableRow } from '../../pdf-components/PdfSplittableTableRow'
import { PdfTableCell } from '../../pdf-components/PdfTableCell'
import { PdfTableHeaderCell } from '../../pdf-components/PdfTableHeaderCell'
import { PdfTableHeaderConfig, pdfTableStyles } from '../../pdf-components/PdfTableStyles'
import { PdfUnsplittableTableRow } from '../../pdf-components/PdfUnsplittableTableRow'
import { getFilteredCollection } from './FormTableUtils'

type TextAlign = 'left' | 'right' | 'center' | 'justify'
type CollectionRecord = Record<string, unknown>

interface TableColumn extends PdfTableHeaderConfig {
  label?: string | Record<string, string>
  /** Salesforce field shown in the column, `type` drives formatting and sorting */
  field: { name?: string; type?: string }
  translate?: boolean
  pdfColumnFlex?: string | number
  pdfCellTextAlign?: TextAlign
  sortByThisColumn?: boolean
}

interface FormTablePdfTypeProps {
  connectedTo?: Array<{ connectedObject?: string; connectedCollection?: string }>
  columns?: TableColumn[]
  pdfTableFontSize?: string | number
  pdfTableTitleFontSize?: string | number
  pdfTableTitleTextAlign?: TextAlign
  pdfTableTitleProps?: string[]
  pdfTableTitlePaddingLeft?: string | number
  pdfTableTitlePaddingRight?: string | number
  pdfTableTitlePaddingTop?: string | number
  pdfTableTitlePaddingBottom?: string | number
  sortOrder?: 'asc' | 'desc'
  tableFilters?: Parameters<typeof getFilteredCollection>[1]
}

interface FormTablePdfProps {
  title?: string
  langVersion: string
  connectedMap: Record<string, { additionalInfo?: Record<string, CollectionRecord[]> }>
  typeProps: FormTablePdfTypeProps
}

const DATE_FIELD_TYPES = ['date', 'datetime']
const NUMERIC_FIELD_TYPES = ['currency', 'percent', 'double', 'int', 'long']

const getTitleStyle = (typeProps: FormTablePdfTypeProps): Style => {
  const {
    pdfTableTitleFontSize,
    pdfTableTitleTextAlign,
    pdfTableTitleProps = [],
    pdfTableTitlePaddingLeft,
    pdfTableTitlePaddingRight,
    pdfTableTitlePaddingTop,
    pdfTableTitlePaddingBottom
  } = typeProps
  const style: Style = {
    ...pdfTableStyles.tableCol,
    fontSize: pdfTableTitleFontSize ? +pdfTableTitleFontSize : pdfDefaultFontSize,
    textAlign: pdfTableTitleTextAlign || 'center'
  }
  if (pdfTableTitleProps.includes('bold')) {
    style.fontWeight = 'bold'
  }
  if (pdfTableTitleProps.includes('italics')) {
    style.fontStyle = 'italic'
  }
  if (pdfTableTitleProps.includes('underline')) {
    style.textDecoration = 'underline'
  }
  if (pdfTableTitlePaddingLeft) {
    style.paddingLeft = Number(pdfTableTitlePaddingLeft)
  }
  if (pdfTableTitlePaddingRight) {
    style.paddingRight = Number(pdfTableTitlePaddingRight)
  }
  if (pdfTableTitlePaddingTop) {
    style.paddingTop = Number(pdfTableTitlePaddingTop)
  }
  if (pdfTableTitlePaddingBottom) {
    style.paddingBottom = Number(pdfTableTitlePaddingBottom)
  }
  return style
}

const isEmptyValue = (value: unknown): boolean =>
  value === undefined || value === null || value === ''

const compareValues = (a: unknown, b: unknown, fieldType?: string): number => {
  if (isEmptyValue(a) || isEmptyValue(b)) {
    return Number(isEmptyValue(a)) - Number(isEmptyValue(b))
  }
  if (fieldType && DATE_FIELD_TYPES.includes(fieldType)) {
    return moment.utc(a as string).diff(moment.utc(b as string))
  }
  if (fieldType && NUMERIC_FIELD_TYPES.includes(fieldType)) {
    return Number(a) - Number(b)
  }
  return String(a).localeCompare(String(b))
}

const sortCollection = (
  collection: CollectionRecord[],
  columns: TableColumn[],
  sortOrder: 'asc' | 'desc'
): CollectionRecord[] => {
  const sortField = columns.find((column) => column.sortByThisColumn)?.field
  if (!sortField?.name) {
    return collection
  }
  const fieldName = sortField.name
  const direction = sortOrder === 'asc' ? 1 : -1
  return [...collection].sort((a, b) => {
    const result = compareValues(a[fieldName], b[fieldName], sortField.type)
    return isEmptyValue(a[fieldName]) || isEmptyValue(b[fieldName]) ? result : result * direction
  })
}

const getColumnStyle = (column: TableColumn): Style => ({
  flex: column.pdfColumnFlex ? Number(column.pdfColumnFlex) : 1
})

export const FormTablePdf = ({
  title,
  langVersion,
  connectedMap,
  typeProps
}: FormTablePdfProps): React.ReactElement => {
  const {
    connectedTo = [],
    columns = [],
    pdfTableFontSize,
    sortOrder = 'asc',
    tableFilters
  } = typeProps

  const { connectedObject, connectedCollection } = connectedTo[0] ?? {}
  const collection =
    connectedObject && connectedCollection
      ? (connectedMap[connectedObject]?.additionalInfo?.[connectedCollection] ?? [])
      : []
  const rows = sortCollection(getFilteredCollection(collection, tableFilters), columns, sortOrder)

  return (
    <View style={pdfTableStyles.table}>
      {Boolean(title) && <Text style={getTitleStyle(typeProps)}>{title}</Text>}
      <PdfUnsplittableTableRow>
        {columns.map((column, index) => (
          <PdfTableHeaderCell key={index} headerConfig={column} style={getColumnStyle(column)}>
            {parseFormLabelText({
              langVersion,
              text: column.label,
              returnString: true
            })}
          </PdfTableHeaderCell>
        ))}
      </PdfUnsplittableTableRow>
      {rows.map((item, rowIndex) => (
        <PdfSplittableTableRow key={rowIndex}>
          {columns.map((column, index) => {
            const { field, pdfCellTextAlign, translate } = column
            const cellStyle: Style = {
              ...getColumnStyle(column),
              textAlign: pdfCellTextAlign || 'center'
            }
            if (pdfTableFontSize) {
              cellStyle.fontSize = +pdfTableFontSize
            }
            if (!field.name) {
              return (
                <PdfTableCell key={index} style={cellStyle}>
                  <Text>NO FIELD CONNECTED</Text>
                </PdfTableCell>
              )
            }
            const fieldValue = item[field.name]
            return (
              <PdfTableCell key={index} style={cellStyle}>
                <Text>
                  {isEmptyValue(fieldValue)
                    ? ''
                    : parseSfFieldValue({
                        value: fieldValue,
                        type: field.type,
                        langVersion,
                        translate
                      })}
                </Text>
              </PdfTableCell>
            )
          })}
        </PdfSplittableTableRow>
      ))}
    </View>
  )
}
